import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Radar, ArrowRight, Clock, RefreshCw, Info } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import {
  CHART_COLORS,
  fmtMonthLabel,
  trimLeadingEmpty,
  filterByRange,
  loadRange,
  saveRange,
  loadCustomRange,
  saveCustomRange,
  validateCustomRange,
} from "@/lib/chartUtils";
import ChartRangePicker from "@/components/ChartRangePicker";

const STAGE_HUMAN = {
  LEAD: "Lead",
  QUALIFIED: "Qualified",
  PROPOSAL: "Proposal",
  NEGOTIATION: "Negotiation",
  WON: "Won",
};

function fmtMoney(v, currency) {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${currency || ""} ${Math.round(v).toLocaleString()}`;
  }
}

function pct(v) {
  return v == null ? "—" : `${Math.round(v * 100)}%`;
}

export default function FunnelPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState(loadRange("rc:funnel:range", "last_12m"));
  const [customRange, setCustomRange] = useState(() => loadCustomRange("rc:funnel:custom"));
  const nav = useNavigate();

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/metrics/latest");
      setData(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const recompute = async () => {
    setBusy(true);
    try {
      await api.post("/metrics/recompute");
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10 text-sm text-slate-500" data-testid="funnel-loading">
        Loading funnel…
      </div>
    );
  }

  if (data.empty || !data.metrics.funnel) {
    return (
      <div className="max-w-3xl mx-auto w-full p-6 lg:p-10" data-testid="funnel-empty-state">
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center flex flex-col items-center">
          <Radar className="h-10 w-10 text-slate-400" />
          <h2 className="mt-4 text-xl font-semibold text-slate-900">No funnel data yet</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-sm">
            Funnel analysis needs at least one snapshot after stage mapping is confirmed.
          </p>
          <button
            onClick={() => nav("/dashboard/connect")}
            data-testid="funnel-setup-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            Set up now
          </button>
        </div>
      </div>
    );
  }

  const m = data.metrics;
  const f = m.funnel;
  const cur = m.currency || "USD";
  const medians = m.stage_medians_days || {};

  return (
    <div className="max-w-6xl mx-auto w-full p-6 lg:p-10 space-y-8" data-testid="funnel-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Funnel X-Ray</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
            Where deals actually die
          </h1>
          <p className="mt-3 text-slate-500 max-w-xl">
            Stage-to-stage conversion, median time per stage, and source-level dynamics over the
            last 12 months. Snapshot {m.metric_date}.
          </p>
        </div>
        <button
          onClick={recompute}
          disabled={busy}
          data-testid="funnel-recompute-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-white ring-1 ring-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
          Recompute
        </button>
      </header>

      {/* Biggest leak / import-artifact / no-history */}
      {f.import_artifact_mode ? (
        <div
          className="rounded-2xl border border-slate-200 bg-slate-50 p-5 flex items-start gap-3"
          data-testid="funnel-import-artifact-notice"
        >
          <Info className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
              Limited history
            </div>
            <div className="mt-1 text-sm font-medium text-slate-900">
              Not enough stage-movement history yet — conversion rates build up as deals move
              through stages. Meanwhile, rates shown below are from closed-deal outcomes.
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Only {f.transitions_observed} stage transitions observed. Threshold for reliable
              conversion analytics: {5}+.
            </div>
          </div>
        </div>
      ) : f.biggest_leak ? (
        <div
          className="rounded-2xl border border-red-200 bg-red-50 p-5 flex items-start gap-3"
          data-testid="funnel-biggest-leak"
        >
          <div className="mt-0.5 h-8 w-8 rounded-full bg-red-600 text-white flex items-center justify-center text-xs font-bold">
            !
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-red-700 font-semibold">
              Biggest leak
            </div>
            <div className="mt-1 text-lg font-semibold text-red-900">
              {f.biggest_leak.sentence}
            </div>
            <div className="text-xs text-red-800 mt-1">
              {f.biggest_leak.reached_next} of {f.biggest_leak.entered} deals passed through in the
              last 12 months.
            </div>
          </div>
        </div>
      ) : (
        <div
          className="rounded-2xl border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-500"
          data-testid="funnel-leak-empty"
        >
          Not enough deals per transition (need ≥{5}) to identify a biggest leak yet.
        </div>
      )}

      {/* Stage conversion table */}
      <section data-testid="funnel-conversions-section">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
          <h2 className="text-xl font-semibold text-slate-900">
            {f.import_artifact_mode ? "Outcome-based rates (limited history)" : "Stage conversions"}
          </h2>
          {f.import_artifact_mode && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold" data-testid="funnel-limited-badge">
              limited history
            </span>
          )}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="grid grid-cols-12 px-6 py-3 border-b border-slate-100 bg-slate-50 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            <div className="col-span-4">{f.import_artifact_mode ? "Stage" : "Transition"}</div>
            <div className="col-span-3 text-right">{f.import_artifact_mode ? "Reached" : "Entered"}</div>
            <div className="col-span-3 text-right">{f.import_artifact_mode ? "Won" : "Reached next"}</div>
            <div className="col-span-2 text-right">Rate</div>
          </div>
          {f.import_artifact_mode ? (
            ["LEAD", "QUALIFIED", "PROPOSAL", "NEGOTIATION"].map((s) => {
              const r = (f.outcome_based_rates || {})[s];
              return (
                <div key={s} className="grid grid-cols-12 px-6 py-4 border-b border-slate-100 last:border-b-0 items-center" data-testid={`funnel-outcome-${s}`}>
                  <div className="col-span-4 text-sm font-medium text-slate-900">{STAGE_HUMAN[s]}</div>
                  <div className="col-span-3 text-right text-sm text-slate-700 number-tabular">{r ? r.reached : "—"}</div>
                  <div className="col-span-3 text-right text-sm text-slate-700 number-tabular">{r ? r.won : "—"}</div>
                  <div className="col-span-2 text-right text-sm font-semibold text-slate-900 number-tabular">
                    {r ? pct(r.rate) : "—"}
                  </div>
                </div>
              );
            })
          ) : (
            f.stage_conversions.map((c, i) => (
              <div
                key={i}
                className="grid grid-cols-12 px-6 py-4 border-b border-slate-100 last:border-b-0 items-center hover:bg-slate-50/60 transition-colors"
                data-testid={`funnel-conversion-${c.from}-${c.to}`}
              >
                <div className="col-span-4 flex items-center gap-2 text-sm font-medium text-slate-900">
                  {STAGE_HUMAN[c.from]}
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                  {STAGE_HUMAN[c.to]}
                </div>
                <div className="col-span-3 text-right text-sm text-slate-700 number-tabular">{c.entered}</div>
                <div className="col-span-3 text-right text-sm text-slate-700 number-tabular">{c.reached_next}</div>
                <div
                  className={`col-span-2 text-right text-sm font-semibold number-tabular ${
                    !c.meaningful ? "text-slate-400" : c.rate < 0.3 ? "text-red-600" : c.rate < 0.6 ? "text-amber-600" : "text-emerald-600"
                  }`}
                >
                  {c.meaningful ? pct(c.rate) : "—"}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Median days per stage */}
      <section data-testid="funnel-medians-section">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Median days per stage</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["LEAD", "QUALIFIED", "PROPOSAL", "NEGOTIATION"].map((s) => (
            <div key={s} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`funnel-median-${s}`}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{STAGE_HUMAN[s]}</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-2xl font-extrabold text-slate-900 number-tabular">
                  {medians[s] != null ? Math.round(medians[s]) : "—"}
                </span>
                {medians[s] != null && <span className="text-xs text-slate-500">days</span>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* By source */}
      <section data-testid="funnel-source-section">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Win rate &amp; sales cycle by source</h2>
        {f.by_source.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="funnel-source-empty">
            Not enough closed deals per source yet (need at least 3 per source).
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-slate-100 bg-slate-50 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              <div className="col-span-4">Source</div>
              <div className="col-span-3 text-right">Win rate</div>
              <div className="col-span-3 text-right">Median cycle</div>
              <div className="col-span-2 text-right">Sample</div>
            </div>
            {f.by_source.map((s) => (
              <div key={s.source} className="grid grid-cols-12 px-6 py-4 border-b border-slate-100 last:border-b-0 items-center">
                <div className="col-span-4 text-sm font-medium text-slate-900">{s.source}</div>
                <div className="col-span-3 text-right text-sm font-semibold text-slate-900 number-tabular">{pct(s.win_rate)}</div>
                <div className="col-span-3 text-right text-sm text-slate-700 number-tabular">
                  {s.median_cycle_days != null ? `${Math.round(s.median_cycle_days)} d` : "—"}
                </div>
                <div className="col-span-2 text-right text-xs text-slate-500 number-tabular">{s.sample}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ACV monthly trend */}
      <section data-testid="funnel-acv-section">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
          <h2 className="text-xl font-semibold text-slate-900">Average deal size (ACV)</h2>
          <ChartRangePicker
            value={range}
            onChange={(id) => { setRange(id); saveRange("rc:funnel:range", id); }}
            customRange={customRange}
            onCustomChange={(r) => { setCustomRange(r); saveCustomRange("rc:funnel:custom", r); }}
            testid="funnel-range-picker"
          />
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 h-64" style={{ minHeight: 240 }}>
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <LineChart data={trimLeadingEmpty(filterByRange(f.acv_monthly, range, (range === "custom" && !validateCustomRange(customRange?.from, customRange?.to)) ? customRange : null), "avg_deal_size", 3)} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
              <CartesianGrid vertical={false} stroke={CHART_COLORS.grid} strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: CHART_COLORS.axis }} tickFormatter={fmtMonthLabel} tickLine={false} axisLine={{ stroke: CHART_COLORS.grid }} />
              <YAxis tick={{ fontSize: 11, fill: CHART_COLORS.axis }} tickFormatter={(v) => (v ? `${Math.round(v / 1000)}k` : "")} width={44} tickLine={false} axisLine={false} />
              <Tooltip
                formatter={(v) => (v == null ? "—" : fmtMoney(v, cur))}
                labelFormatter={fmtMonthLabel}
                contentStyle={{ fontSize: 12, border: `1px solid ${CHART_COLORS.grid}`, borderRadius: 8 }}
              />
              <Line type="monotone" dataKey="avg_deal_size" stroke={CHART_COLORS.primary} strokeWidth={2} dot={{ r: 3, fill: CHART_COLORS.primary }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {err && <div className="text-sm text-red-600" data-testid="funnel-error">{err}</div>}
    </div>
  );
}
